import { createToken, Lexer, CstParser } from 'chevrotain'

/**
 * Chevrotain-based parser para comandos RPCode dentro de corchetes.
 * Sintaxis soportada (compatibilidad estricta):
 *   [NOMBRE]
 *   [NOMBRE:param1, param2]
 *   [NOMBRE:key=value, other="cadena con \"escapes\"", count=123]
 *   - Sin anidación.
 *   - Valores sin comillas pueden incluir espacios internos y terminan en ',' o ']'.
 *   - Comillas dobles con escapes: \", \\n, \\t, \\\\.
 */

// Tokens
const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /[ \t\r\n]+/,
  group: Lexer.SKIPPED,
})

const Name = createToken({
  name: 'Name',
  pattern: /[A-Z_]+/,
})

const Ident = createToken({
  name: 'Ident',
  pattern: /[a-zA-Z_][a-zA-Z0-9_]*/,
})

const Colon = createToken({
  name: 'Colon',
  pattern: /:/,
})

const Comma = createToken({
  name: 'Comma',
  pattern: /,/,
})

const Equals = createToken({
  name: 'Equals',
  pattern: /=/,
})

const StringLiteral = createToken({
  name: 'StringLiteral',
  // "..." with escapes
  pattern: /"(?:\\.|[^"\\])*"/,
})

const NumberLiteral = createToken({
  name: 'NumberLiteral',
  pattern: /-?\d+/,
})

const FreeValue = createToken({
  name: 'FreeValue',
  // Cualquier cosa excepto coma o cierre de corchete. Incluye espacios internos.
  pattern: /[^,\]]+/,
})

// Orden importante: tokens más específicos primero
const allTokens = [
  WhiteSpace,
  StringLiteral,
  NumberLiteral,
  Colon,
  Comma,
  Equals,
  Name,
  Ident,
  FreeValue,
]

const lexer = new Lexer(allTokens, { ensureOptimizations: false }) // Desactivar optimizaciones: FreeValue usa complemento [^,\\]] que no es optimizable

// Grammar
class RPCommandParser extends CstParser {
  constructor() {
    super(allTokens)

    this.RULE('command', () => {
      this.CONSUME(Name)
      this.OPTION(() => {
        this.CONSUME(Colon)
        this.SUBRULE(this.paramList)
      })
    })

    this.RULE('paramList', () => {
      this.SUBRULE(this.param)
      this.MANY(() => {
        this.CONSUME(Comma)
        this.SUBRULE2(this.param)
      })
    })

    this.RULE('param', () => {
      this.OR([
        { ALT: () => this.SUBRULE(this.keyEquals) },
        { ALT: () => this.SUBRULE(this.positional) },
      ])
    })

    this.RULE('keyEquals', () => {
      this.CONSUME(Ident)
      this.CONSUME(Equals)
      this.SUBRULE(this.value)
    })

    this.RULE('positional', () => {
      this.SUBRULE(this.value)
    })

    this.RULE('value', () => {
      this.OR([
        { ALT: () => this.CONSUME(StringLiteral) },
        { ALT: () => this.CONSUME(NumberLiteral) },
        {
          ALT: () => {
            this.CONSUME(FreeValue)
            this.MANY(() => {
              this.CONSUME2(FreeValue)
            })
          },
        },
      ])
    })

    this.performSelfAnalysis()
  }
}

const parserInstance = new RPCommandParser()
const BaseVisitor = parserInstance.getBaseCstVisitorConstructorWithDefaults()

class RPVisitor extends BaseVisitor {
  constructor() {
    super()
    this.validateVisitor()
  }

  command(ctx) {
    const nameTok = ctx.Name[0]
    const params = ctx.paramList ? this.visit(ctx.paramList[0]) : []
    return {
      name: nameTok.image,
      params,
    }
  }

  paramList(ctx) {
    const out = [this.visit(ctx.param[0])]
    if (ctx.param.length > 1) {
      for (let i = 1; i < ctx.param.length; i++) {
        out.push(this.visit(ctx.param[i]))
      }
    }
    return out
  }

  param(ctx) {
    if (ctx.keyEquals) return this.visit(ctx.keyEquals[0])
    return this.visit(ctx.positional[0])
  }

  keyEquals(ctx) {
    const key = ctx.Ident[0].image
    const val = this.visit(ctx.value[0])
    return { kind: 'named', key, ...val }
  }

  positional(ctx) {
    const val = this.visit(ctx.value[0])
    return { kind: 'positional', ...val }
  }

  value(ctx) {
    if (ctx.StringLiteral) {
      const raw = ctx.StringLiteral[0].image
      return { valueType: 'string', raw, value: unescapeString(raw) }
    }
    if (ctx.NumberLiteral) {
      const raw = ctx.NumberLiteral[0].image
      return { valueType: 'number', raw, value: parseInt(raw, 10) }
    }
    if (ctx.FreeValue) {
      const parts = ctx.FreeValue.map((t) => t.image)
      const raw = parts.join(' ')
      return { valueType: 'free', raw, value: raw.trim() }
    }
    return { valueType: 'free', raw: '', value: '' }
  }
}

function unescapeString(raw) {
  const inner = raw.slice(1, -1)
  return inner.replace(/\\(.)/g, (_m, ch) => {
    switch (ch) {
      case 'n':
        return '\n'
      case 'r':
        return '\r'
      case 't':
        return '\t'
      case '"':
        return '"'
      case '\\':
        return '\\'
      default:
        return ch
    }
  })
}

/**
 * Parsea el contenido dentro de [ ... ] y devuelve AST + errores.
 * @param {string} input - contenido sin los corchetes exteriores
 * @returns {{ast: {name:string, params:Array}, lexErrors:any[], parseErrors:any[]}}
 */
export function parseRPCommand(input) {
  const lexResult = lexer.tokenize(input)
  parserInstance.input = lexResult.tokens
  const cst = parserInstance.command()
  const lexErrors = lexResult.errors
  const parseErrors = parserInstance.errors

  let ast = null
  if (lexErrors.length === 0 && parseErrors.length === 0) {
    const visitor = new RPVisitor()
    ast = visitor.visit(cst)
  }

  return { ast, lexErrors, parseErrors }
}